import { Button, Center, Group, Kbd, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { spotlight } from "@mantine/spotlight";
import { IconMagnifierOutline18, IconRadarOutline18 } from "nucleo-ui-outline-18";
import { useTranslation } from "react-i18next";

import { ICON_SIZE } from "@mountayaapp/theme";

import { useModKey } from "../search/modKey";

/**
 * GLYPH_FRAME and GLYPH_SIZE size the disc that opens the block and the glyph inside
 * it. The block is the whole page rather than an element within one, so it leads at a
 * scale well above the icons in the chrome, and the tinted disc gives the centered
 * column something to hang from instead of a glyph floating in white space. The disc
 * takes its full width as its radius so it reads as a circle rather than a squircle at
 * this size. The glyph is the radar sweep that found nothing — a dead URL as lost
 * signal, the same reading the marketing site's error page opens with.
 */
const GLYPH_FRAME = 80;
const GLYPH_SIZE = 36;

/**
 * COLUMN_WIDTH holds the copy to a measure that keeps the lead to two balanced lines
 * instead of letting it run the full width the missing sidebar leaves behind.
 * SEARCH_WIDTH widens the search trigger past the label it carries, so the sole
 * action on the page reads as the search field it opens rather than as a chip.
 */
const COLUMN_WIDTH = 520;
const SEARCH_WIDTH = 320;

/**
 * MIN_HEIGHT fills the region below the header, so the block centers in the viewport
 * rather than sitting in its top third above a screen of dead space. It reads the
 * header's height from the shell's own custom property instead of restating it, and
 * subtracts the main region's padding, which sits inside this element's box.
 */
const MIN_HEIGHT =
  "calc(100dvh - var(--app-shell-header-offset, 0px) - var(--mantine-spacing-md) * 2)";

/**
 * DocsNotFound is the not-found view rendered when a pathname matches no authored
 * page. It is prerendered to `error.html` for the static host's error document and
 * also shown on the client for an unmatched location, so it has to read correctly
 * under every shape a dead URL arrives in — with a language prefix, with an
 * unrecognized one, or with none at all. It renders framed by the header alone — a
 * location with no page belongs to no section, so there is no reading order for the
 * sidebar to rail — over a centered empty state: the glyph and the status code above
 * the heading and its lead, then the way out.
 *
 * That way out is the header's own search trigger at the scale of a page's primary
 * action: search is what recovers a mistyped or retired URL, and repeating the
 * control the reader already knows from the header — same treatment, same shortcut
 * hint — means the page teaches nothing new at the moment it is least welcome. Widened
 * that far it needs its glyph and label to sit together against the left edge with the
 * shortcut hint pushed to the right, so the two ride in the label as one group and the
 * hint is the only section: with just those two children the trigger reads like the
 * search field it opens rather than spacing its label out across the middle.
 *
 * The `404` eyebrow is the status code itself rather than prose: it names the condition
 * the same way in every language, so it carries no locale key.
 */
export const DocsNotFound = () => {
  const { t } = useTranslation("docs");
  const modKey = useModKey();

  return (
    <Center mih={MIN_HEIGHT}>
      <Stack align="center" gap="lg" maw={COLUMN_WIDTH}>
        <ThemeIcon color="gray" radius={GLYPH_FRAME} size={GLYPH_FRAME} variant="light">
          <IconRadarOutline18 size={GLYPH_SIZE} />
        </ThemeIcon>

        <Stack align="center" gap="xs">
          <Text c="dimmed" fw={700} size="sm" tt="uppercase">
            404
          </Text>
          <Title order={1} ta="center">
            {t("notFound.title")}
          </Title>
          <Text c="dimmed" size="lg" ta="center">
            {t("notFound.body")}
          </Text>
        </Stack>

        <Button
          justify="space-between"
          onClick={spotlight.open}
          rightSection={<Kbd>{modKey} K</Kbd>}
          size="md"
          variant="default"
          w={SEARCH_WIDTH}
        >
          <Group gap="xs">
            <IconMagnifierOutline18 size={ICON_SIZE.sm} />
            {t("search.trigger")}
          </Group>
        </Button>
      </Stack>
    </Center>
  );
};
